import { useEffect, useCallback, useMemo } from 'react';
import { useInfiniteQuery, UseInfiniteQueryOptions, UseInfiniteQueryResult, QueryKey, QueryFunctionContext } from 'react-query';
import { isTruthy } from '@/utility';
import ExceptionManager from '@/managers/exception-manager';

export function useInfiniteQueryProvider<TQueryFnData = unknown, TError = unknown, TData = TQueryFnData>(
	queryKey: Array<string>,
	queryFn: (context: QueryFunctionContext<QueryKey, unknown>) => Promise<TQueryFnData>,
	options: Omit<UseInfiniteQueryOptions<TQueryFnData, TError, TData>, 'queryKey' | 'queryFn'>,
	forceEnabled: boolean = false
): UseInfiniteQueryResult<TData, TError> {
	const queryKeys = useMemo(() => [...queryKey], [queryKey]);

	const updatedOptions: UseInfiniteQueryOptions<TQueryFnData, TError, TData> = {
		...options,
		enabled: forceEnabled ? true : options.enabled
	};

	const queryResult = useInfiniteQuery<TQueryFnData, TError, TData>({
		queryKey: queryKeys,
		queryFn,
		...updatedOptions
	});

	const refetchIfNeeded = useCallback(() => {
		if ((updatedOptions?.refetchOnWindowFocus || updatedOptions?.refetchOnMount === 'always') && isTruthy(updatedOptions?.enabled)) {
			queryResult.refetch().catch(ExceptionManager.errorHandler);
		}
	}, [updatedOptions?.enabled, updatedOptions?.refetchOnWindowFocus, updatedOptions?.refetchOnMount, forceEnabled, queryResult.refetch]);

	useEffect(() => {
		refetchIfNeeded();

		window.addEventListener('focus', refetchIfNeeded);

		return () => {
			window.removeEventListener('focus', refetchIfNeeded);
		};
	}, [refetchIfNeeded]);

	return queryResult;
}
